import { toEpoch } from '@confluence/macro-utils/entry-points/dateUtils.ts';

import type {
	InlineTasksQuerySortColumn,
	GetTasksReportQuery$variables,
} from './__generated__/GetTasksReportQuery.graphql.ts';
import type { ParsedMacroParams } from './hooks/useParsedMacroParams.ts';
import { STATUS_MAP as STATUS_MAP_INTERNAL } from './tasksReportStatus.ts';

export const STATUS_MAP: {
	readonly complete: 'CHECKED';
	readonly incomplete: 'UNCHECKED';
} = STATUS_MAP_INTERNAL;

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/**
 * Maps REST macro `sortBy` param values to GraphQL `InlineTasksQuerySortColumn` enum values.
 * The GraphQL enum only supports: DUE_DATE, ASSIGNEE, PAGE_TITLE.
 * REST values that have no direct equivalent (description, completedate) fall back to null
 * so the backend applies its default sort (DUE_DATE ascending).
 */
export const SORT_COLUMN_MAP: Record<string, InlineTasksQuerySortColumn> = {
	duedate: 'DUE_DATE',
	assignee: 'ASSIGNEE',
	location: 'PAGE_TITLE',
};

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

/** Parse comma-separated values into a string array, trimming whitespace and filtering empty strings. */
export const parseCommaSeparatedValues = (value: string | undefined): string[] | undefined => {
	if (!value) return undefined;
	const raw = value.split(',');
	const result: string[] = [];
	for (const rawValue of raw) {
		const trimmed = rawValue.trim();
		if (trimmed) {
			result.push(trimmed);
		}
	}
	return result.length ? result : undefined;
};

export const CURRENT_SPACE_TOKEN = 'currentSpace()';
export const SELF_SPACE_TOKEN = '@self';

export const toLongId = (value: string): number | undefined => {
	const normalizedValue = value.startsWith('~') ? value.slice(1) : value;
	if (!/^\d+$/.test(normalizedValue)) {
		return undefined;
	}

	const parsedId = Number.parseInt(normalizedValue, 10);
	return Number.isNaN(parsedId) ? undefined : parsedId;
};

export const toLongIds = (values: string[] | undefined, currentSpaceId?: number): number[] => {
	if (!values?.length) {
		return [];
	}

	const parsedIds: number[] = [];
	for (const value of values) {
		if (value === CURRENT_SPACE_TOKEN || value === SELF_SPACE_TOKEN) {
			if (currentSpaceId !== undefined) {
				parsedIds.push(currentSpaceId);
			}
			continue;
		}

		const parsedId = toLongId(value);
		if (parsedId !== undefined) {
			parsedIds.push(parsedId);
		}
	}

	return parsedIds;
};

// ---------------------------------------------------------------------------
// Query variable builder
// ---------------------------------------------------------------------------

type TaskQueryVariables = GetTasksReportQuery$variables['taskQuery'];

/**
 * Build the `InlineTasksByMetadata` variable object from parsed macro params and
 * pre-resolved space IDs.
 *
 * Uses cursor-based pagination: `first` is set to `pageSize` so the server returns
 * one page of results at a time. Callers pass `after` to advance through pages.
 * Sorting is handled server-side via `sortParameters` when a supported column is
 * active (DUE_DATE, ASSIGNEE, PAGE_TITLE). The `labels` column has no server-side
 * sort equivalent and is handled client-side within the current page only.
 */
export function buildTaskQueryMetadata(
	params: ParsedMacroParams,
	spaceIds: number[],
	spaceKeys: string[],
	after: string | null = null,
): TaskQueryVariables {
	const requestedStatus = params.status;
	const mappedStatus = STATUS_MAP[requestedStatus] ?? STATUS_MAP.incomplete;
	const pageIds = toLongIds(parseCommaSeparatedValues(params.pages));

	const assigneeAccountIds = parseCommaSeparatedValues(params.assignees);
	const creatorAccountIds = parseCommaSeparatedValues(params.creators);

	const hasDueDate = params.duedateFrom != null || params.duedateTo != null;
	const hasCreatedDate = params.createddateFrom != null || params.createddateTo != null;

	// Only pass sortParameters for columns the server can sort — labels has no
	// server-side equivalent and falls back to the server's default order.
	const sortColumn = params.sortBy ? (SORT_COLUMN_MAP[params.sortBy] ?? null) : null;

	const dueDate = hasDueDate
		? {
				startDate: toEpoch(params.duedateFrom),
				endDate: toEpoch(params.duedateTo),
			}
		: null;

	const createdDateRange = hasCreatedDate
		? {
				startDate: toEpoch(params.createddateFrom),
				endDate: toEpoch(params.createddateTo),
			}
		: null;

	return {
		first: params.pageSize,
		after,
		spaceIds: spaceIds.length ? spaceIds : null,
		spaceKeys: spaceKeys.length ? spaceKeys : null,
		pageIds: pageIds.length ? pageIds : null,
		status: mappedStatus,
		accountIds:
			assigneeAccountIds?.length || creatorAccountIds?.length
				? { assigneeAccountIds, creatorAccountIds }
				: null,
		dueDate,
		createdDateRange,
		sortParameters: sortColumn
			? {
					sortColumn,
					// Legacy macro semantics: reverseSort=false keeps default descending order,
					// reverseSort=true flips to ascending.
					sortOrder: params.reverseSort ? 'ASCENDING' : 'DESCENDING',
				}
			: null,
	};
}
